/**
 * 抖音 x-tt-verify-passport-decision 解析器
 * 修复版 - 正确处理 UTF-8 编码的中文字符
 */

export interface VerifyPassportDecision {
    account_flow: "verify" | string;
    biz_params: unknown;
    common_params: CommonParams;
    encrypt_uid: string;
    event_params: EventParams;
    extra_params: ExtraParams;
    pack_verify_way: boolean;
    schema: string;
    url: string;
    user_info: UserInfo;
    verify_way_name_list: string;
}

export interface CommonParams {
    copywriting: string;
    copywriting_key: string;
    gateway_abtest_group: string;
    new_verify_flow: string;
}

export interface CopywritingContent {
    title: string;
    desc: string;
}

export interface EventParams {
    log_id: string;
    verify_reason: string;
    verify_scene: string;
}

export interface ExtraParams {
    log_id: string;
}

export interface UserInfo {
    avatar_url: string;
    nickname: string;
    mobile_info: string | null;
}

export interface ParsedCommonParams extends CommonParams {
    parsed_copywriting: CopywritingContent;
}

export interface ParsedVerifyDecision {
    account_flow: "verify" | string;
    biz_params: unknown;
    common_params: ParsedCommonParams;
    encrypt_uid: string;
    event_params: EventParams;
    extra_params: ExtraParams;
    pack_verify_way: boolean;
    schema: string;
    url: string;
    user_info: UserInfo;
    verify_way_name_list: string;
    verify_ways: VerifyWay[];
    verify_title: string;
    verify_desc: string;
}

export type VerifyWay =
    | "mobile_sms_verify"
    | "mobile_up_sms_verify"
    | "face_verify"
    | "idcard_verify"
    | "password_verify"
    | string;

/**
 * 将乱码字符串（如 "èº«ä»½"）恢复为正确的中文字符（如 "身份"）
 */
function utf8Encoding(str: string): string {
    try {
        // 方法1: 使用 TextEncoder/TextDecoder 进行编码转换
        // 将错误解析的字符重新编码为字节，然后用 UTF-8 解码
        const bytes = new Uint8Array(str.split("").map((c) => c.charCodeAt(0) & 0xff));
        const decoder = new TextDecoder("utf-8");
        const result = decoder.decode(bytes);

        // 检查解码是否成功（如果包含中文常用字范围）
        if (/[\u4e00-\u9fa5]/.test(result)) {
            return result;
        }
        return str;
    } catch {
        return str;
    }
}

/**
 * 解码 Unicode 转义序列 (\uXXXX)
 */
function decodeUnicodeEscapes(str: string): string {
    return str.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
    });
}

/**
 * 解析 x-tt-verify-passport-decision 响应头
 * @param headerValue - 响应头的原始值
 * @returns 解析后的结构化数据
 */
export function parseVerifyPassportDecision(headerValue: string | null | undefined): ParsedVerifyDecision | null {
    if (!headerValue) {
        return null;
    }

    try {
        // 1. 去除外层引号包裹
        let cleanValue = headerValue.trim();
        if (
            (cleanValue.startsWith('"') && cleanValue.endsWith('"')) ||
            (cleanValue.startsWith("'") && cleanValue.endsWith("'"))
        ) {
            cleanValue = cleanValue.slice(1, -1);
        }

        // 2. 解码 Unicode 转义字符 (\u0026 -> &, \u4e2d -> 中)
        let decoded = decodeUnicodeEscapes(cleanValue);

        // 3. 尝试修复 UTF-8 编码问题
        decoded = utf8Encoding(decoded);

        // 4. 解析 JSON
        const parsed: VerifyPassportDecision = JSON.parse(decoded);

        // 5. 修复各个字段的编码问题
        const fixedParsed: VerifyPassportDecision = {
            ...parsed,
            common_params: {
                ...parsed.common_params,
                copywriting: utf8Encoding(parsed.common_params.copywriting),
            },
            user_info: {
                ...parsed.user_info,
                nickname: utf8Encoding(parsed.user_info.nickname),
            },
        };

        // 6. 丰富解析后的数据
        return enrichParsedData(fixedParsed);
    } catch (error) {
        console.error("[parseVerifyPassportDecision] 解析失败:", error);
        return null;
    }
}

/**
 * 丰富解析后的数据
 */
function enrichParsedData(data: VerifyPassportDecision): ParsedVerifyDecision {
    // 解析 copywriting JSON（处理双重转义）
    let parsedCopywriting: CopywritingContent = { title: "", desc: "" };
    try {
        const copywritingStr = data.common_params.copywriting;

        // 先解码 JSON 转义，再解析
        const cleanedCopywriting = copywritingStr
            .replace(/\\"/g, '"') // \" -> "
            .replace(/\\n/g, "\n") // \n -> 换行
            .replace(/\\t/g, "\t") // \t -> 制表
            .replace(/\\\\/g, "\\"); // \\ ->
        const parsed = JSON.parse(cleanedCopywriting);
        parsedCopywriting = {
            title: utf8Encoding(parsed.title || ""),
            desc: utf8Encoding(parsed.desc || ""),
        };
    } catch (e) {
        console.warn("[parseVerifyPassportDecision] copywriting 解析失败:", e);
        // 备用：直接提取
        try {
            const titleMatch = data.common_params.copywriting.match(/"title":"([^"]+)"/);
            const descMatch = data.common_params.copywriting.match(/"desc":"([^"]+)"/);
            parsedCopywriting = {
                title: utf8Encoding(titleMatch?.[1] || ""),
                desc: utf8Encoding(descMatch?.[1] || ""),
            };
        } catch {
            // 忽略
        }
    }

    // 解析验证方式列表
    const verifyWays: VerifyWay[] = data.verify_way_name_list
        ? data.verify_way_name_list.split(",").map((s) => s.trim() as VerifyWay)
        : [];

    return {
        ...data,
        common_params: {
            ...data.common_params,
            parsed_copywriting: parsedCopywriting,
        },
        verify_ways: verifyWays,
        verify_title: parsedCopywriting.title,
        verify_desc: parsedCopywriting.desc,
    };
}

// ==================== 辅助函数 ====================

export function needsVerification(decision: ParsedVerifyDecision | null): boolean {
    return decision?.account_flow === "verify";
}

export function getVerifyUrl(decision: ParsedVerifyDecision | null): string | null {
    return decision?.url || null;
}

export function getAppSchema(decision: ParsedVerifyDecision | null): string | null {
    return decision?.schema || null;
}

export function getUserNickname(decision: ParsedVerifyDecision | null): string | null {
    return decision?.user_info?.nickname || null;
}

export function getVerifyPrompt(decision: ParsedVerifyDecision | null): CopywritingContent {
    return {
        title: decision?.verify_title || "",
        desc: decision?.verify_desc || "",
    };
}

export function supportsVerifyWay(decision: ParsedVerifyDecision | null, way: VerifyWay): boolean {
    return decision?.verify_ways?.includes(way) || false;
}

export function getRecommendedVerifyWay(decision: ParsedVerifyDecision | null): VerifyWay | null {
    if (!decision?.verify_ways?.length) return null;

    const priority: VerifyWay[] = [
        "mobile_sms_verify",
        "mobile_up_sms_verify",
        "face_verify",
        "idcard_verify",
        "password_verify",
    ];

    for (const way of priority) {
        if (decision.verify_ways.includes(way)) {
            return way;
        }
    }

    return decision.verify_ways[0];
}
